import * as React from 'react';
import { Card, Typography, Space, Empty, Tag, Button, Divider } from 'antd';
import {
    MailOutlined,
    PaperClipOutlined,
    DownloadOutlined,
    RollbackOutlined,
    ArrowRightOutlined,
    DeleteOutlined,
    InboxOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import './MailPreview.scss';

const { Title, Text, Paragraph } = Typography;

interface Email {
    _id: string;
    from: string;
    to: string[];
    cc?: string[];
    bcc?: string[];
    subject: string;
    body?: string;
    mailContent?: {
        subject: string;
        body: string;
        attachments?: string[];
    };
    mailStatus?: string;
    createdAt: string;
    attachments?: string[];
    tags?: Array<{ type: string; name: string }>;
}

interface MailPreviewProps {
    email: Email | null;
}

const MailPreview: React.FC<MailPreviewProps> = ({ email }) => {
    if (!email) {
        return (
            <div className="mail-preview-empty">
                <Empty
                    image={<MailOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
                    description={
                        <div>
                            <Text type="secondary" style={{ fontSize: 16 }}>
                                No email selected
                            </Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 14 }}>
                                Select an email to read
                            </Text>
                        </div>
                    }
                />
            </div>
        );
    }

    const subject = email.mailContent?.subject || email.subject || 'No Subject';
    const body = email.mailContent?.body || email.body || '';
    const attachments = email.attachments || email.mailContent?.attachments || [];
    const emailDate = moment(email.createdAt);

    return (
        <div className="mail-preview">
            <Card className="mail-preview-card">
                <div className="mail-preview-header">
                    <Title level={4} className="mail-preview-subject">
                        {subject}
                    </Title>
                    <div className="mail-preview-actions">
                        <Button type="text" icon={<RollbackOutlined />} title="Reply" />
                        <Button type="text" icon={<ArrowRightOutlined />} title="Forward" />
                        <Button type="text" icon={<InboxOutlined />} title="Archive" />
                        <Button type="text" icon={<DeleteOutlined />} title="Delete" danger />
                    </div>
                </div>
                <Divider style={{ margin: '12px 0' }} />
                <div className="mail-preview-meta">
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <div className="mail-preview-from">
                            <Text strong>From:</Text>
                            <Text style={{ marginLeft: 8 }}>{email.from || 'Unknown'}</Text>
                        </div>
                        {email.to && email.to.length > 0 && (
                            <div className="mail-preview-to">
                                <Text strong>To:</Text>
                                <Text style={{ marginLeft: 8 }}>{email.to.join(', ')}</Text>
                            </div>
                        )}
                        {email.cc && email.cc.length > 0 && (
                            <div className="mail-preview-cc">
                                <Text strong>CC:</Text>
                                <Text style={{ marginLeft: 8 }}>{email.cc.join(', ')}</Text>
                            </div>
                        )}
                        {email.bcc && email.bcc.length > 0 && (
                            <div className="mail-preview-bcc">
                                <Text strong>BCC:</Text>
                                <Text style={{ marginLeft: 8 }}>{email.bcc.join(', ')}</Text>
                            </div>
                        )}
                        <div className="mail-preview-date">
                            <Text type="secondary">{emailDate.format('MMMM DD, YYYY [at] h:mm A')}</Text>
                        </div>
                        {email.tags && email.tags.length > 0 && (
                            <div className="mail-preview-tags">
                                {email.tags.map((tag, index) => (
                                    <Tag key={index} color="blue">
                                        {tag.type}: {tag.name}
                                    </Tag>
                                ))}
                            </div>
                        )}
                    </Space>
                </div>
                {attachments.length > 0 && (
                    <>
                        <Divider style={{ margin: '12px 0' }} />
                        <div className="mail-preview-attachments">
                            <Text strong>
                                <PaperClipOutlined /> Attachments ({attachments.length})
                            </Text>
                            <div className="mail-attachments-list">
                                {attachments.map((attachment, index) => (
                                    <div key={index} className="mail-attachment-item">
                                        <PaperClipOutlined />
                                        <Text>{attachment}</Text>
                                        <Button
                                            type="link"
                                            icon={<DownloadOutlined />}
                                            size="small"
                                            style={{ padding: 0, marginLeft: 8 }}
                                        >
                                            Download
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}
                <Divider style={{ margin: '12px 0' }} />
                <div className="mail-preview-body">
                    <Paragraph>
                        <div dangerouslySetInnerHTML={{ __html: body }} />
                    </Paragraph>
                </div>
            </Card>
        </div>
    );
};

export default MailPreview;
